import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BookOpen, ChevronRight, Eye, Loader2, PenLine, RotateCcw, Save, Send, Sparkles, Wand2, X } from "lucide-react";
import { api, isAdmin, type HelpAnswer, type HelpSource, type Role } from "../lib/api";

/**
 * Ask anything.
 *
 * The assistant reads the user guide and, where the server has an Anthropic key, the live
 * plan too: a flight's stand and the reason for it, the settings, the history, the feeds.
 * Every answer says what it looked at, so nothing has to be taken on trust, and the guide
 * sections behind it can be opened in full.
 *
 * Administrators also get "Improve the guide": the questions people asked that the guide
 * could not answer, and an editor that drafts and saves a page without leaving the app.
 */

const EXAMPLES = [
  "Why is this flight on that stand?",
  "How do I move an aircraft to another stand?",
  "What is the turn time for a Q400 here?",
  "How many security lanes will we need at 07:00?",
  "Is the bag feed working?",
];

interface Said {
  role: "user" | "assistant";
  content: string;
  /** Set on an assistant turn: what it checked and which guide sections it used. */
  looked?: string[];
  sources?: HelpSource[];
  /** True when the guide had nothing on this, so the answer came from the app itself. */
  gap?: boolean;
}

export function Help({ onClose, role, onSupport }: { onClose: () => void; role: Role; onSupport?: () => void }) {
  const [question, setQuestion] = useState("");
  const [said, setSaid] = useState<Said[]>([]);
  const [page, setPage] = useState<string | null>(null);
  const [improving, setImproving] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  const thread = useRef<HTMLDivElement>(null);

  const index = useQuery({ queryKey: ["help"], queryFn: api.help });
  const guidePage = useQuery({ queryKey: ["help-page", page], queryFn: () => api.helpPage(page!), enabled: !!page });

  const ask = useMutation({
    mutationFn: (q: string) =>
      api.helpAsk(
        q,
        said.slice(-6).map(({ role: r, content }) => ({ role: r, content })),
      ),
    onSuccess: (a: HelpAnswer) =>
      setSaid((prev) => [
        ...prev,
        {
          role: "assistant",
          content: a.answer ?? (a.sources.length ? "" : "I could not find anything about that. Try other words, or ask a planner."),
          looked: a.looked,
          sources: a.sources,
          gap: !a.inGuide,
        },
      ]),
    onError: (e: Error) => setSaid((prev) => [...prev, { role: "assistant", content: `That did not work: ${e.message}` }]),
  });

  const send = (q: string) => {
    const text = q.trim();
    if (!text || ask.isPending) return;
    setSaid((prev) => [...prev, { role: "user", content: text }]);
    setQuestion("");
    ask.mutate(text);
  };

  useEffect(() => {
    box.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  useEffect(() => {
    thread.current?.scrollTo({ top: thread.current.scrollHeight, behavior: "smooth" });
  }, [said, ask.isPending]);

  const title = page ? guidePage.data?.title ?? "Guide" : improving ? "Improve the guide" : "Help";

  return (
    /* Above the board and everything it pops: the hover card sits at z-60 and was
       appearing over this panel, which made the app look like it had lost focus. */
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/50" onMouseDown={onClose}>
      <aside className="flex h-full w-full max-w-xl flex-col border-l border-line bg-panel shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <header className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3">
          {page || improving ? (
            <button
              className="rounded p-1 text-ink-2 hover:bg-panel-2"
              onClick={() => (page ? setPage(null) : setImproving(false))}
              aria-label="Back"
            >
              <ArrowLeft className="size-4" />
            </button>
          ) : (
            <BookOpen className="size-4 text-ink-3" />
          )}
          <div className="flex-1 text-[15px] font-bold">{title}</div>
          {!page && !improving && isAdmin(role) && (
            <button className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[12px] text-ink-2 hover:border-ink-3" onClick={() => setImproving(true)}>
              <PenLine className="size-3.5" /> Improve the guide
            </button>
          )}
          <button className="rounded p-1 text-ink-2 hover:bg-panel-2" onClick={onClose} aria-label="Close help">
            <X className="size-4" />
          </button>
        </header>

        {page ? (
          <div className="scroll-thin flex-1 overflow-auto px-4 py-3">
            {guidePage.isLoading ? <Loader2 className="size-5 animate-spin text-ink-3" /> : <Markdown text={guidePage.data?.markdown ?? ""} />}
          </div>
        ) : improving ? (
          <ImproveGuide onOpen={setPage} />
        ) : (
          <>
            <div ref={thread} className="scroll-thin flex-1 overflow-auto px-4 py-3">
              {index.data && !index.data.ready && (
                <p className="mb-3 text-[13px] text-bad">The guide is not installed on this server, so the assistant has less to read.</p>
              )}

              {!said.length && (
                <div className="mb-4">
                  <div className="mb-1 flex items-center gap-1.5 text-[13.5px] font-semibold">
                    <Sparkles className="size-4 text-ink-3" />
                    {index.data?.written ? "Ask about the app, or about today" : "Ask about the app"}
                  </div>
                  <p className="mb-3 text-[12.5px] text-ink-3">
                    {index.data?.written
                      ? "It reads the guide and the live plan: a flight's stand and why, the settings, what happened last week, whether the feeds are healthy. It can explain how to change something, but it never changes anything itself."
                      : "It answers from the user guide. Live answers about today's plan need an Anthropic API key on the server."}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {EXAMPLES.map((q) => (
                      <button
                        key={q}
                        type="button"
                        className="rounded-full border border-line px-2.5 py-1 text-[11.5px] text-ink-2 hover:border-ink-3"
                        onClick={() => send(q)}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {said.map((m, i) =>
                m.role === "user" ? (
                  <div key={i} className="mb-3 flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-panel-2 px-3 py-2 text-[13px]">{m.content}</div>
                  </div>
                ) : (
                  <div key={i} className="mb-4">
                    {!!m.looked?.length && (
                      <div className="mb-1.5 flex flex-wrap items-center gap-1 text-[11px] text-ink-3">
                        <Eye className="size-3" />
                        {m.looked.map((l, n) => (
                          <span key={n}>
                            {n > 0 && <span className="mx-0.5">·</span>}
                            {l}
                          </span>
                        ))}
                      </div>
                    )}
                    {m.content && <Markdown text={m.content} />}
                    {!!m.sources?.length && (
                      <div className="mt-1.5 space-y-1">
                        {m.sources.slice(0, 3).map((s) => (
                          <button
                            key={`${s.page}-${s.anchor}`}
                            className="flex w-full items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-left text-[12px] text-ink-2 hover:border-ink-3"
                            onClick={() => setPage(s.page)}
                          >
                            <ChevronRight className="size-3.5 shrink-0 text-ink-3" />
                            <span className="truncate">
                              {s.heading}
                              <span className="ml-1.5 text-ink-3">{s.pageTitle}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ),
              )}

              {ask.isPending && (
                <div className="mb-3 flex items-center gap-2 text-[12.5px] text-ink-3">
                  <Loader2 className="size-3.5 animate-spin" /> Looking…
                </div>
              )}

              {!said.length && (
                <div className="mt-6 border-t border-line pt-4">
                  <div className="mb-2 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">The whole guide</div>
                  <div className="space-y-1">
                    {index.data?.pages.map((p) => (
                      <button key={p.page} className="block w-full rounded-lg border border-line px-3 py-2 text-left hover:border-ink-3" onClick={() => setPage(p.page)}>
                        <div className="text-[13px] font-semibold">{p.title}</div>
                        <div className="truncate text-[11.5px] text-ink-3">{p.headings.slice(0, 4).join(" · ")}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              className="shrink-0 border-t border-line px-4 py-3"
              onSubmit={(e) => {
                e.preventDefault();
                send(question);
              }}
            >
              <div className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5 ring-1 ring-info/30 focus-within:ring-2 focus-within:ring-info">
                <input
                  ref={box}
                  className="w-full bg-transparent text-[13.5px] outline-none"
                  placeholder={said.length ? "Ask a follow-up…" : "Ask a question, e.g. why is WS181 on Stand 7?"}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
                <button type="submit" className="shrink-0 rounded p-1 text-ink-2 hover:bg-panel-2 disabled:opacity-40" disabled={!question.trim() || ask.isPending} aria-label="Ask">
                  <Send className="size-4" />
                </button>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11.5px] text-ink-3">
                {said.length > 0 && (
                  <button type="button" className="hover:text-ink-2" onClick={() => setSaid([])}>
                    Start again
                  </button>
                )}
                {/* When the answer is "that is a bug", the way to say so is one click away. */}
                {onSupport && (
                  <button type="button" className="ml-auto hover:text-ink-2" onClick={onSupport}>
                    Report a problem or request a feature
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </aside>
    </div>
  );
}

/**
 * Keeping the guide current, for administrators. The questions nobody could answer are the
 * list of what to write next; the assistant drafts it, a person reads it and saves it.
 * Pages saved here live in the database, so they survive a publish.
 */
function ImproveGuide({ onOpen }: { onOpen: (page: string) => void }) {
  const qc = useQueryClient();
  const asked = useQuery({ queryKey: ["help-questions"], queryFn: api.helpQuestions });
  const index = useQuery({ queryKey: ["help"], queryFn: api.help });
  const [editing, setEditing] = useState<{ page: string; title: string; markdown: string; isNew: boolean } | null>(null);
  const [instruction, setInstruction] = useState("");
  const [error, setError] = useState("");

  const draft = useMutation({
    mutationFn: () => api.helpDraft(instruction, editing && !editing.isNew ? editing.page : undefined),
    onSuccess: (r) => {
      setError("");
      const drafted = r.markdown;
      if (!drafted) return setError("Nothing came back. Try saying more about what the section should cover.");
      setEditing((e) => (e ? { ...e, markdown: `${e.markdown.trim()}\n\n${drafted}`.trim() } : { page: "", title: "", markdown: drafted, isNew: true }));
    },
    onError: (e: Error) => setError(e.message),
  });

  const save = useMutation({
    mutationFn: () => api.helpSavePage(editing!.page, editing!.title, editing!.markdown),
    onSuccess: () => {
      setEditing(null);
      setInstruction("");
      void qc.invalidateQueries({ queryKey: ["help"] });
      void qc.invalidateQueries({ queryKey: ["help-questions"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const revert = useMutation({
    mutationFn: (name: string) => api.helpRevertPage(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["help"] });
      void qc.invalidateQueries({ queryKey: ["help-questions"] });
    },
  });

  const open = async (page: string) => {
    const p = await api.helpPage(page);
    setEditing({ page: p.page, title: p.title, markdown: p.markdown, isNew: false });
  };

  if (editing)
    return (
      <div className="scroll-thin flex flex-1 flex-col overflow-auto px-4 py-3">
        <div className="mb-2 flex items-center gap-2">
          <input
            className="w-32 rounded-lg border border-line bg-transparent px-2 py-1 text-[12.5px] outline-none"
            placeholder="page-name"
            value={editing.page}
            disabled={!editing.isNew}
            onChange={(e) => setEditing({ ...editing, page: e.target.value })}
          />
          <input
            className="flex-1 rounded-lg border border-line bg-transparent px-2 py-1 text-[12.5px] outline-none"
            placeholder="Page title"
            value={editing.title}
            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
          />
        </div>
        <div className="mb-2 flex items-center gap-2">
          <input
            className="flex-1 rounded-lg border border-line bg-transparent px-2 py-1 text-[12.5px] outline-none"
            placeholder="What should this cover? e.g. how bag counts reach the board"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
          />
          <button
            className="flex shrink-0 items-center gap-1 rounded-lg border border-line px-2 py-1 text-[12px] hover:border-ink-3 disabled:opacity-40"
            disabled={instruction.trim().length < 4 || draft.isPending}
            onClick={() => draft.mutate()}
          >
            {draft.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Wand2 className="size-3.5" />} Draft
          </button>
        </div>
        <textarea
          className="scroll-thin min-h-72 flex-1 rounded-lg border border-line bg-transparent p-2.5 font-mono text-[12px] leading-relaxed outline-none"
          value={editing.markdown}
          onChange={(e) => setEditing({ ...editing, markdown: e.target.value })}
        />
        {error && <div className="mt-1.5 text-[12px] text-bad">{error}</div>}
        <div className="mt-2 flex items-center gap-2">
          <button
            className="flex items-center gap-1 rounded-lg bg-ink px-2.5 py-1.5 text-[12.5px] font-semibold text-panel disabled:opacity-40"
            disabled={!editing.page.trim() || !editing.title.trim() || editing.markdown.trim().length < 10 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} Save and publish
          </button>
          <button className="rounded-lg border border-line px-2.5 py-1.5 text-[12.5px] hover:border-ink-3" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <span className="text-[11.5px] text-ink-3">Saved pages go live at once, for everyone.</span>
        </div>
      </div>
    );

  return (
    <div className="scroll-thin flex-1 overflow-auto px-4 py-3">
      <p className="mb-3 text-[12.5px] text-ink-3">
        Every question put to the assistant is kept. The ones the guide could not answer are below: they are the honest list of what the guide is missing. Pages
        you save here are stored in the database, so a publish does not lose them.
      </p>

      <div className="mb-4">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Asked, and not in the guide</div>
          <button className="text-[12px] text-info hover:underline" onClick={() => setEditing({ page: "", title: "", markdown: "", isNew: true })}>
            New page
          </button>
        </div>
        {!asked.data?.gaps.length ? (
          <p className="text-[12.5px] text-ink-3">Nothing yet. Either the guide is covering everything, or nobody has asked.</p>
        ) : (
          <div className="space-y-1">
            {asked.data.gaps.map((g) => (
              <button
                key={g.question}
                className="block w-full rounded-lg border border-line px-3 py-2 text-left hover:border-ink-3"
                onClick={() => {
                  setInstruction(`Write the guide section that answers: ${g.question}`);
                  setEditing({ page: "", title: "", markdown: "", isNew: true });
                }}
              >
                <div className="text-[13px]">{g.question}</div>
                <div className="text-[11.5px] text-ink-3">
                  asked {g.times} {g.times === 1 ? "time" : "times"} · last {new Date(g.lastAt).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Pages</div>
        <div className="space-y-1">
          {index.data?.pages.map((p) => (
            <div key={p.page} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2">
              <button className="min-w-0 flex-1 text-left" onClick={() => onOpen(p.page)}>
                <div className="truncate text-[13px] font-semibold">
                  {p.title}
                  {p.edited && <span className="ml-2 rounded bg-panel-2 px-1.5 py-0.5 text-[10.5px] font-normal text-ink-3">edited here</span>}
                </div>
                <div className="truncate text-[11.5px] text-ink-3">{p.headings.slice(0, 4).join(" · ")}</div>
              </button>
              <button className="shrink-0 rounded p-1 text-ink-3 hover:bg-panel-2" onClick={() => void open(p.page)} aria-label={`Edit ${p.title}`}>
                <PenLine className="size-3.5" />
              </button>
              {p.edited && (
                <button
                  className="shrink-0 rounded p-1 text-ink-3 hover:bg-panel-2"
                  onClick={() => revert.mutate(p.page)}
                  aria-label={`Undo changes to ${p.title}`}
                  title="Back to the page as shipped"
                >
                  <RotateCcw className="size-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Lately asked</div>
        <div className="divide-y divide-line">
          {asked.data?.recent.slice(0, 25).map((q) => (
            <div key={q.id} className="flex items-baseline gap-2 py-1.5 text-[12px]">
              <span className="w-20 shrink-0 text-ink-3">{new Date(q.at).toLocaleDateString()}</span>
              <span className="min-w-0 flex-1 truncate text-ink-2">{q.question}</span>
              {!q.answered && <span className="shrink-0 text-[11px] text-bad">no answer</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Just enough Markdown for the guide: headings, numbered and bulleted lists,
 * tables, bold, inline code and links.
 */
function Markdown({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  const lines = text.split(/\r?\n/);
  let i = 0;
  const inline = (s: string, key: string) => {
    const parts: React.ReactNode[] = [];
    const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s))) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      const t = m[0];
      if (t.startsWith("**")) parts.push(<b key={`${key}-${m.index}`}>{t.slice(2, -2)}</b>);
      else if (t.startsWith("`")) parts.push(<code key={`${key}-${m.index}`} className="rounded bg-panel-2 px-1 py-0.5 text-[12px]">{t.slice(1, -1)}</code>);
      else {
        const label = t.slice(1, t.indexOf("]"));
        parts.push(<span key={`${key}-${m.index}`} className="font-medium">{label}</span>);
      }
      last = m.index + t.length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };

  while (i < lines.length) {
    const before = i;
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    if (line.startsWith("### ") || line.startsWith("## ")) {
      const level = line.startsWith("### ") ? 3 : 2;
      blocks.push(
        <div key={i} className={clsx("mt-3 mb-1 font-bold", level === 2 ? "text-[14px]" : "text-[13px]")}>
          {inline(line.replace(/^#+\s*/, ""), `h${i}`)}
        </div>,
      );
      i++;
    } else if (/^\s*[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*[-*]\s/, ""));
      blocks.push(
        <ul key={i} className="mb-2 list-disc space-y-1 pl-5 text-[13px] text-ink-2">
          {items.map((t, n) => (
            <li key={n}>{inline(t, `u${i}-${n}`)}</li>
          ))}
        </ul>,
      );
    } else if (/^\s*\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*\d+\.\s/, ""));
      blocks.push(
        <ol key={i} className="mb-2 list-decimal space-y-1 pl-5 text-[13px] text-ink-2">
          {items.map((t, n) => (
            <li key={n}>{inline(t, `o${i}-${n}`)}</li>
          ))}
        </ol>,
      );
    } else if (line.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.startsWith("|")) {
        const cells = lines[i]!.split("|").slice(1, -1).map((c) => c.trim());
        if (!cells.every((c) => /^:?-+:?$/.test(c))) rows.push(cells);
        i++;
      }
      const [head, ...body] = rows;
      blocks.push(
        <table key={i} className="mb-2 w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              {head?.map((c, n) => (
                <th key={n} className="px-1.5 py-1 text-left text-[11px] tracking-wide text-ink-3 uppercase">
                  {inline(c, `th${i}-${n}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, n) => (
              <tr key={n} className="border-b border-line align-top">
                {r.map((c, k) => (
                  <td key={k} className="px-1.5 py-1 text-ink-2">
                    {inline(c, `td${i}-${n}-${k}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
    } else {
      const para: string[] = [];
      while (i < lines.length && lines[i]!.trim() && !/^(#|\s*[-*]\s|\s*\d+\.\s|\|)/.test(lines[i]!)) para.push(lines[i++]!);
      blocks.push(
        <p key={i} className="mb-2 text-[13px] text-ink-2">
          {inline(para.join(" "), `p${i}`)}
        </p>,
      );
    }
    // Every branch must consume at least one line; this stops a bad edit hanging the page.
    if (i === before) i++;
  }
  return <div>{blocks}</div>;
}
